import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FireOutlined,
  StarFilled,
  BulbOutlined,
  QuestionCircleOutlined,
  ThunderboltOutlined,
  BookOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { Button, message } from 'antd';
import { useRecoilValue } from 'recoil';
import { getReport } from '@/services/report';
import { CapabilitySuggestionDTO, RadarDataDTO, ReportItemDTO } from '@/types/report';
import { currentWaitingReportState } from '@/store/message';
import { getQuestionnaireInfo, submitQuestionnaire } from '@/services/questionnaire';
import { QuestionnaireAnswers, QuestionnaireSchema } from '@/types';
import Questionnaire from '@/components/Questionnaire';

type RadarItem = {
  label: string;
  value: number;
};

interface RadarChartProps {
  items: RadarItem[];
  maxValue?: number;
}

const RadarChart = ({ items, maxValue = 10 }: RadarChartProps) => {
  if (!items || items.length === 0) {
    return null;
  }

  const centerX = 130;
  const centerY = 90;
  const radius = 80;
  const levels = 4;
  const angleStep = (Math.PI * 2) / items.length;

  const getPoint = (value: number, index: number, maxRadius = radius) => {
    const angle = -Math.PI / 2 + angleStep * index;
    const r = (Math.max(0, Math.min(value, maxValue)) / maxValue) * maxRadius;
    const x = centerX + r * Math.cos(angle);
    const y = centerY + r * Math.sin(angle);
    return { x, y };
  };

  const gridPolygons = Array.from({ length: levels }, (_, levelIndex) => {
    const level = ((levelIndex + 1) / levels) * radius;
    const points = items
      .map((_, index) => {
        const { x, y } = getPoint(maxValue, index, level);
        return `${x},${y}`;
      })
      .join(' ');

    return (
      <polygon key={levelIndex} points={points} fill="none" stroke="#e5e7eb" strokeWidth={0.8} />
    );
  });

  const dataPoints = items
    .map((item, index) => {
      const { x, y } = getPoint(item.value, index);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg viewBox="0 0 260 180" className="w-full h-full">
      {/* 网格多边形 */}
      <g>{gridPolygons}</g>

      {/* 轴线 */}
      <g>
        {items.map((_, index) => {
          const { x, y } = getPoint(maxValue, index);
          return (
            <line
              key={index}
              x1={centerX}
              y1={centerY}
              x2={x}
              y2={y}
              stroke="#e5e7eb"
              strokeWidth={0.8}
            />
          );
        })}
      </g>

      {/* 数据多边形 */}
      <polygon
        points={dataPoints}
        fill="rgba(79, 70, 229, 0.18)"
        stroke="#4f46e5"
        strokeWidth={2}
      />

      {/* 顶点圆点 */}
      <g>
        {items.map((item, index) => {
          const { x, y } = getPoint(item.value, index);
          return <circle key={index} cx={x} cy={y} r={3} fill="#4f46e5" />;
        })}
      </g>

      {/* 文本标签 */}
      <g>
        {items.map((item, index) => {
          const labelRadius = radius + 8;
          const angle = -Math.PI / 2 + angleStep * index;
          const x = centerX + labelRadius * Math.cos(angle);
          const y = centerY + labelRadius * Math.sin(angle);

          let textAnchor: 'start' | 'middle' | 'end' = 'middle';
          if (Math.cos(angle) > 0.3) textAnchor = 'start';
          if (Math.cos(angle) < -0.3) textAnchor = 'end';

          return (
            <text
              key={index}
              x={x}
              y={y}
              textAnchor={textAnchor}
              dominantBaseline="middle"
              fontSize={8}
              fill="#4b5563"
            >
              {item.label}
            </text>
          );
        })}
      </g>
    </svg>
  );
};

const Report = () => {
  const navigate = useNavigate();
  const currentWaitingReport = useRecoilValue(currentWaitingReportState);
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState<ReportItemDTO[]>([]);
  const [averageScore, setAverageScore] = useState(0);
  const [generationStatus, setGenerationStatus] = useState<
    'pending' | 'generating' | 'completed' | 'skipped' | 'failed'
  >('generating');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const { interviewNumber } = useParams();
  const [schema, setSchema] = useState<QuestionnaireSchema | null>(null);
  const [overallComment, setOverallComment] = useState<string>('');
  const [strengths, setStrengths] = useState<string[]>([]);
  const [improvements, setImprovements] = useState<string[]>([]);
  const [radarData, setRadarData] = useState<RadarDataDTO | null>(null);
  const [capabilitySuggestions, setCapabilitySuggestions] =
    useState<CapabilitySuggestionDTO | null>(null);

  const slotConfig = useMemo(() => {
    if (!interviewNumber) {
      return null;
    }
    const config = window.config?.questionnaireSlots?.reportBottom;
    if (config?.enable) {
      return config;
    }
    return null;
  }, [interviewNumber]);

  useEffect(() => {
    const interviewNum = interviewNumber as unknown as number;
    if (
      currentWaitingReport === null ||
      (currentWaitingReport &&
        currentWaitingReport[interviewNum] &&
        currentWaitingReport[interviewNum] === 'completed')
    ) {
      getReport(interviewNum).then((report) => {
        setGenerationStatus(report.generationStatus);
        setStatusMessage(report.message);
        setReportData(report.questionGroups);
        setAverageScore(report.averageScore);
        // 综合评价（后端未接入前使用 mock 数据兜底）
        setOverallComment(report.overallComment || '');
        setStrengths(report.strengths || []);
        setImprovements(report.improvements || []);
        setRadarData(report.radarData);
        setCapabilitySuggestions(
          report.capabilitySuggestions || {
            shortTermKnowledge: [],
            longTermGrowth: [],
          },
        );
        setLoading(false);
      });
    }
  }, [interviewNumber, currentWaitingReport]);

  // 加载问卷信息
  useEffect(() => {
    if (!slotConfig) {
      return;
    }
    if (!slotConfig.enable) {
      return;
    }
    const { questionnaireNumber } = slotConfig;

    const loadQuestionnaire = async () => {
      try {
        setLoading(true);
        if (isNaN(questionnaireNumber)) {
          navigate('/');
          return;
        }

        // 获取问卷信息（包含 Schema、答案和 readOnly 状态）
        const questionnaireInfo = await getQuestionnaireInfo(questionnaireNumber);

        if (questionnaireInfo.readOnly) {
          return;
        }
        setSchema(questionnaireInfo.schema);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('加载问卷失败:', error);
      } finally {
        setLoading(false);
      }
    };

    loadQuestionnaire();
  }, [slotConfig]);

  // 处理提交
  const handleSubmit = async (submittedAnswers: QuestionnaireAnswers) => {
    if (!slotConfig) {
      return;
    }
    if (!slotConfig.enable) {
      return;
    }
    const { questionnaireNumber } = slotConfig;

    try {
      const result = await submitQuestionnaire(questionnaireNumber, submittedAnswers);

      if (result.success) {
        message.success(result.message || '提交成功');
        setSchema(null);
      }
    } catch (error) {
      message.error('提交失败，请稍后重试');
      // eslint-disable-next-line no-console
      console.error('提交问卷失败:', error);
    }
  };

  const radarItems: RadarItem[] = useMemo(() => {
    if (!radarData) {
      return [];
    }
    return [
      { label: '技术深度', value: radarData.technicalDepth },
      { label: '项目经验', value: radarData.projectExperience },
      { label: '表达能力', value: radarData.communication },
      { label: '岗位契合度', value: radarData.jobFit },
      { label: '逻辑能力', value: radarData.logicalAbility },
    ];
  }, [radarData]);

  const getScoreColor = (score: number) => {
    if (score >= 99) return '#FF8000';
    if (score >= 90) return '#A335EE';
    if (score >= 80) return '#0070DD';
    if (score >= 70) return '#95ec69';
    return '#9D9D9D';
  };

  const getScoreLevel = (score: number) => {
    if (score >= 99) return '完美';
    if (score >= 90) return '优秀';
    if (score >= 80) return '良好';
    if (score >= 70) return '中等';
    return '需提高';
  };

  const handleBackHome = () => {
    navigate('/');
  };

  // 渲染不同生成状态的 UI
  const renderStatusView = () => {
    if (loading || generationStatus === 'generating' || generationStatus === 'pending') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-500 to-purple-600 via-purple-500 flex items-center justify-center px-5 py-10">
          <div className="text-center max-w-[500px]">
            <div className="w-20 h-20 border-[6px] border-white/30 border-t-white rounded-full mx-auto mb-8 animate-spin"></div>
            <h2 className="text-[28px] font-bold text-white mb-3">
              {generationStatus === 'pending' ? '等待生成报告...' : '正在生成面试报告...'}
            </h2>
            <p className="text-base text-white/90 mb-6">
              {statusMessage || '小灵正在分析你的回答，评估各项能力'}
            </p>
            <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white rounded-full animate-progress"></div>
            </div>
          </div>
        </div>
      );
    }

    if (generationStatus === 'skipped') {
      return (
        <div className="min-h-screen bg-slate-50 py-10 px-4 flex items-center justify-center">
          <div className="max-w-[600px] mx-auto text-center">
            <div className="bg-white rounded-3xl p-10 shadow-sm">
              <ExclamationCircleOutlined className="text-6xl text-amber-500 mb-6" />
              <h2 className="text-[32px] font-bold text-slate-800 mb-4">报告未生成</h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                {statusMessage || '当前不满足报告生成条件，请稍后再试'}
              </p>
              <Button type="primary" size="large" onClick={handleBackHome}>
                返回首页
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (generationStatus === 'failed') {
      return (
        <div className="min-h-screen bg-slate-50 py-10 px-4 flex items-center justify-center">
          <div className="max-w-[600px] mx-auto text-center">
            <div className="bg-white rounded-3xl p-10 shadow-sm">
              <CloseCircleOutlined className="text-6xl text-red-500 mb-6" />
              <h2 className="text-[32px] font-bold text-slate-800 mb-4">报告生成失败</h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                {statusMessage || '报告生成过程中出现错误，请稍后重试'}
              </p>
              <div className="flex gap-4 justify-center">
                <Button type="default" size="large" onClick={handleBackHome}>
                  返回首页
                </Button>
                <Button
                  type="primary"
                  size="large"
                  onClick={() => {
                    setLoading(true);
                    const interviewNum = interviewNumber as unknown as number;
                    getReport(interviewNum).then((report) => {
                      setGenerationStatus(report.generationStatus);
                      setStatusMessage(report.message);
                      setReportData(report.questionGroups);
                      setAverageScore(report.averageScore);
                      setLoading(false);
                    });
                  }}
                >
                  重新加载
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // 如果不在 completed 状态，显示对应的状态视图
  if (loading || generationStatus !== 'completed') {
    return renderStatusView();
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 scrollbar-thin">
      <div className="max-w-[1440px] mx-auto">
        <div className="text-center mb-12 z-[1]">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-full text-sm font-semibold text-indigo-600 mb-5">
            <FireOutlined className="text-lg" />
            <span>面试完成</span>
          </div>
          <h1 className="text-[42px] font-extrabold text-slate-800 mb-2 tracking-tight">
            面试评估报告
          </h1>
          <p className="text-[15px] text-slate-500">{new Date().toLocaleDateString('zh-CN')}</p>
        </div>

        {/* 综合评价 */}
        <div className="mb-12">
          {/* <h2 className="text-[28px] font-bold text-slate-800 mb-4 flex items-center gap-2">
            <FileTextOutlined />
            <span>综合评价</span>
          </h2> */}

          {/* 中部：左侧综合评分，右侧优势/提升点 */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-6 mb-6">
            {/* 左侧：综合评分卡片 */}
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-sm flex flex-col items-center justify-center gap-5">
              <div
                className="relative w-40 h-40"
                style={{ '--score-color': getScoreColor(averageScore) } as React.CSSProperties}
              >
                <svg className="w-full h-full scale-x-[-1]" viewBox="0 0 200 200">
                  <circle cx="100" cy="100" r="90" fill="none" stroke="#e2e8f0" strokeWidth="12" />
                  <circle
                    cx="100"
                    cy="100"
                    r="90"
                    fill="none"
                    stroke="var(--score-color)"
                    strokeWidth="12"
                    strokeDasharray={`${averageScore * 5.65} 565`}
                    strokeLinecap="round"
                    transform="rotate(-90 100 100)"
                  />
                </svg>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                  <div
                    className="text-5xl font-extrabold text-slate-800 leading-none mb-1.5"
                    style={{ color: getScoreColor(averageScore) }}
                  >
                    {averageScore}
                  </div>
                  <div className="text-[13px] font-semibold text-slate-500">综合评分</div>
                </div>
              </div>
              <div className="text-center">
                <div
                  className="text-xl font-bold mb-1.5"
                  style={{ color: getScoreColor(averageScore) }}
                >
                  {getScoreLevel(averageScore)}
                </div>
              </div>
            </div>

            {/* 右侧：综合优势 & 提升点卡片 */}
            <div className="flex flex-col gap-4">
              <div className="bg-white rounded-3xl p-6 shadow-sm">
                <h3 className="text-base font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <StarFilled className="text-amber-500" />
                  <span>优势点</span>
                </h3>
                <ul className="list-disc list-inside space-y-2 text-sm text-slate-700">
                  {strengths.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-3xl p-6 shadow-sm">
                <h3 className="text-base font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <BulbOutlined className="text-indigo-500" />
                  <span>提升点</span>
                </h3>
                <ul className="list-disc list-inside space-y-2 text-sm text-slate-700">
                  {improvements.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* 底部：一句话总结卡片 */}
          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <p className="text-[15px] leading-relaxed text-slate-800">{overallComment}</p>
          </div>
        </div>

        {/* 多维能力分析 */}
        <div className="mb-12">
          <h2 className="text-[28px] text-center font-bold text-slate-800 mb-6">多维能力分析</h2>
          <div className="flex flex-col lg:flex-row gap-6 items-stretch">
            <div className="w-full max-w-[450px] bg-white rounded-3xl p-8 shadow-sm z-[1]">
              <p className="text-sm text-slate-500 mb-5">
                从技术深度、项目经验、表达能力与岗位契合度等维度，对你的整体表现进行量化评估。
              </p>
              <div className="aspect-square max-w-[360px] mx-auto">
                <RadarChart items={radarItems} />
              </div>
            </div>
            <div className="w-full bg-white rounded-3xl p-8 shadow-sm z-[1]">
              <h3 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <BulbOutlined className="text-indigo-500" />
                <span>提升建议</span>
              </h3>
              <div className="mb-6">
                <div className="text-sm font-semibold text-slate-600 mb-2">短期知识点补全</div>
                <ul className="list-disc list-inside space-y-1.5 text-sm text-slate-700">
                  {capabilitySuggestions?.shortTermKnowledge.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-600 mb-2">中长期提升</div>
                <ul className="list-disc list-inside space-y-1.5 text-sm text-slate-700">
                  {capabilitySuggestions?.longTermGrowth.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {reportData.length ? (
          <div className="mb-12">
            <h2 className="text-[28px] text-center font-bold text-slate-800 mb-6">详细分析</h2>
            <div className="flex flex-col gap-6">
              {reportData.map((item, index) => (
                <div
                  key={item.id}
                  className="bg-white rounded-3xl p-8 shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 z-[1]"
                >
                  <div className="flex justify-between items-center mb-5">
                    <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                      问题 {index + 1}
                    </div>
                    <div
                      className="px-4 py-1.5 text-white rounded-full text-sm font-bold"
                      style={{ backgroundColor: getScoreColor(item.score) }}
                    >
                      {item.score} 分
                    </div>
                  </div>

                  <div className="mb-5">
                    <h3 className="text-lg font-bold text-slate-800 leading-relaxed flex items-start gap-2">
                      <QuestionCircleOutlined className="mt-1 text-indigo-600" />
                      <span>{item.questions[0]}</span>
                    </h3>
                    <div className="ml-6 mt-2 flex flex-col gap-2">
                      {item.questions.slice(1).map((question, index) => (
                        <div key={index}>
                          <span className="text-sm text-slate-500">
                            追问 {index + 1}：{question}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5 p-5 bg-slate-50 rounded-2xl">
                    <h4 className="text-sm font-semibold text-slate-500 mb-3">你的回答：</h4>
                    <p className="text-[15px] leading-loose text-slate-800">{item.answers[0]}</p>
                    <div className="mt-2 flex flex-col gap-2">
                      {item.answers.slice(1).map((answer, index) => (
                        <div key={index}>
                          <span className="text-sm text-slate-500">
                            回复追问 {index + 1}：{answer}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5 p-5 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 rounded-2xl border-2 border-indigo-500/10">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white rounded-xl text-[13px] font-semibold text-indigo-600 mb-3">
                      <ThunderboltOutlined className="text-sm" />
                      <span>小灵评价</span>
                    </div>
                    <p className="text-[15px] leading-relaxed text-slate-800">{item.feedback}</p>
                  </div>

                  <div className="mb-5">
                    <h4 className="text-sm font-semibold text-slate-500 mb-3">关键词覆盖：</h4>
                    <div className="flex flex-wrap gap-2">
                      {item.keywords.map((keyword, idx) => (
                        <span
                          key={idx}
                          className="px-3.5 py-1.5 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-2xl text-[13px] font-semibold"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-slate-500 mb-3 flex items-center gap-2">
                      <BookOutlined />
                      <span>需要加强的知识点：</span>
                    </h4>
                    <ul className="list-none pl-0">
                      {item.improvementPoints.map((point, idx) => (
                        <li
                          key={idx}
                          className="py-2.5 px-4 bg-slate-50 border-l-[3px] border-indigo-600 rounded-lg mb-2 text-sm text-slate-800"
                        >
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {schema ? (
          <Questionnaire
            schema={schema}
            onSubmit={handleSubmit}
            cancelLabel="返回首页"
            onCancel={handleBackHome}
          />
        ) : (
          <div className="flex gap-4 justify-center">
            <Button type="default" onClick={handleBackHome}>
              返回首页
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Report;
